import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Radar } from "lucide-react";
import {
  getWorkflowConsistency,
  getWorkflowReadiness,
} from "../../services/compliance-api";
import type {
  WorkflowConsistencyResponse,
  WorkflowReadinessResponse,
} from "../../types/compliance";

function scoreClass(score: number) {
  if (score >= 85) return "text-emerald-700";
  if (score >= 60) return "text-amber-700";
  return "text-red-700";
}

function decisionClass(decision: string) {
  if (decision === "ready_for_approval") return "bg-emerald-100 text-emerald-700";
  if (decision === "blocked") return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-700";
}

export default function WorkflowV2Insights({
  workflowId,
  shipmentType = "eu_import",
}: {
  workflowId: string;
  shipmentType?: string;
}) {
  const [readiness, setReadiness] = useState<WorkflowReadinessResponse | null>(null);
  const [consistency, setConsistency] = useState<WorkflowConsistencyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [readinessResult, consistencyResult] = await Promise.all([
          getWorkflowReadiness(workflowId, shipmentType),
          getWorkflowConsistency(workflowId),
        ]);
        if (!cancelled) {
          setReadiness(readinessResult);
          setConsistency(consistencyResult);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "V2 intelligence failed");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [workflowId, shipmentType]);

  if (loading) {
    return (
      <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-700">
        <Loader2 size={15} className="mr-2 inline animate-spin" />
        Loading v2 readiness and consistency...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-100 bg-red-50 p-3 text-sm text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-blue-100 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Radar size={18} className="text-blue-700" />
          <h3 className="font-semibold text-gray-900">V2 Readiness</h3>
        </div>
        {readiness && (
          <span className={`rounded-full px-2 py-1 text-xs font-semibold ${decisionClass(readiness.decision)}`}>
            {readiness.decision.replace(/_/g, " ")}
          </span>
        )}
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-md bg-gray-50 p-3">
          <p className="text-xs uppercase text-gray-500">Shipment Score</p>
          <p className={`text-2xl font-bold ${scoreClass(readiness?.shipment_readiness_score ?? 0)}`}>
            {readiness?.shipment_readiness_score ?? 0}%
          </p>
        </div>
        <div className="rounded-md bg-gray-50 p-3">
          <p className="text-xs uppercase text-gray-500">Audit Score</p>
          <p className={`text-2xl font-bold ${scoreClass(readiness?.audit_readiness_score ?? 0)}`}>
            {readiness?.audit_readiness_score ?? 0}%
          </p>
        </div>
        <div className="rounded-md bg-gray-50 p-3">
          <p className="text-xs uppercase text-gray-500">Consistency</p>
          <p className={`text-2xl font-bold ${scoreClass(Math.round((consistency?.overall_score ?? 0) * 100))}`}>
            {Math.round((consistency?.overall_score ?? 0) * 100)}%
          </p>
        </div>
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <div>
          <p className="text-xs font-semibold uppercase text-gray-500">Missing Evidence</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {readiness?.missing_required_documents.length ? (
              readiness.missing_required_documents.map((documentType) => (
                <span key={documentType} className="rounded-full bg-amber-100 px-2 py-1 text-xs text-amber-700">
                  {documentType.replace(/_/g, " ")}
                </span>
              ))
            ) : (
              <span className="inline-flex items-center gap-1 text-sm text-emerald-700">
                <CheckCircle2 size={15} /> Required evidence present
              </span>
            )}
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase text-gray-500">Discrepancies</p>
          <div className="mt-2 space-y-1">
            {consistency?.discrepancies.length ? (
              consistency.discrepancies.map((item, index) => (
                <p key={`${String(item.risk_type)}-${index}`} className="inline-flex items-center gap-1 text-sm text-red-700">
                  <AlertTriangle size={15} /> {String(item.risk_type).replace(/_/g, " ")}
                </p>
              ))
            ) : (
              <span className="inline-flex items-center gap-1 text-sm text-emerald-700">
                <CheckCircle2 size={15} /> No cross-document conflicts
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

